/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {ColDef, ColumnState} from 'ag-grid-community';
import isUndefined from 'lodash.isundefined';
import {idMap} from '@ion/core/src/utils/ObjectUtil';

// latest version of AG Grid filter key mapped to older versions.
const updatedKeyMap = {
  true   : 'agTextColumnFilter',
  text   : 'agTextColumnFilter',
  number : 'agNumberColumnFilter',
  date   : 'agDateColumnFilter'
};

/**
 * @stateToDefinitions
 *
 * Given a collection of current column state, and a collection of the state's
 * corresponding column definitions, returns the column definitions with the
 * provided state applied.
 *
 * ```typescript
 *
 *     import {stateToDefinitions} from '@ion/dls/src/grid/';
 *
 *     // apply the current column state to the column definitions ...
 *     const updated: ColDef[] = stateToDefinitions(state, definitions);
 * ```
 *
 * @param   {ColumnState[]} The current state of the column definitions that is
 *          to be applied.
 * @param   {ColDef[]} The list of column definitions for which the state is to
 *          be applied.
 * @returns {ColDef[]} The provided column definitions with the provided state
 *          applied to them.
 */
export const stateToDefinitions = (state: ColumnState[], definitions: ColDef[], resolver?: Function): ColDef[] => {
  if (state?.length) {
    const definitionsMap = idMap(definitions, 'field');
    const columnStateMap = idMap(state, 'colId');
    const updated: ColDef[] = [];
    let additions: ColDef[] = [];
    state?.forEach((update: ColumnState) => {
      const item = update as any;
      const {
        colId,
        sort,
        sortIndex,
        hide,
        width,
        flex
      }                = item;
      const definition = resolver ? resolver(definitionsMap, colId) : definitionsMap[colId];
      if (definition) {
        updated.push(update ? {...definition, sort, hide, width, flex, sortIndex} : definition);
      }
    });
    additions = definitions.filter(({field}: ColDef): boolean => !columnStateMap[field]);
    return resolveDefinitions(additions?.length ? updated.concat(additions) : updated);
  }
  return resolveDefinitions(definitions);
};

/**
 * Given a collection of the state's corresponding column definitions, returns the
 * column definitions with the updation as per the enterprice edition of ag grid.
 *
 * ```typescript
 *
 *  import {resolveDefinitions} from '@ion/dls/src/grid/';
 *
 *  const updated: ColDef[] = resolveDefinitions(definitions);
 * ```
 * @param definitions The column definitions of ag grids
 * @returns Modified column definitions
 */
export const resolveDefinitions = (definitions: ColDef[]): ColDef[] => {
  const updatedDefinitions = definitions.map(definition => {
    // if there filter is falsy we shouldn't show the menu at all since we aren't,
    // and likely won't, use the other tabs of the menu.
    const {
      filter,
      pinned,
      filterParams,
      suppressHeaderContextMenu,
      suppressHeaderMenuButton
    } = definition;
    const updatedKey = updatedKeyMap[filter?.toString()];
    if (updatedKey) {
      definition.filter = updatedKey;
    }
    if (!filter && !updatedKey) {
      definition.filter = false;
    }
    definition.filterParams = {
      maxNumConditions : 1,
      ...filterParams
    };
    definition.suppressHeaderContextMenu = suppressHeaderContextMenu ?? true;
    definition.suppressHeaderMenuButton  = suppressHeaderMenuButton ?? true;
    // If the column definition doesn't contain a 'pinned' property
    // then we need to set 'null' to that to avoid it from locking
    // at the left or right side of the grid...
    if (isUndefined(pinned)) {
      definition.pinned = null;
    }
    return definition;
  });
  return updatedDefinitions;
};

/**
 * @definitionsToState
 *
 * Given a collection of column definitions, returns the state of each column.
 *
 * ```typescript
 *
 *     import {definitionsToState} from '@ion/dls/src/grids';
 *
 *     // retrieve the current state of all columns ...
 *     const currentState: ColumnState[] = definitionsToState(definitions);
 * ```
 *
 * @param   {ColumnState[]} The current state of the column definitions that is
 *          to be applied.
 * @returns {ColDef[]} The list of column definitions for which the state is to
 *          be extracted.
 */
export const definitionsToState = (definitions: ColDef[]): ColumnState[] => {
  const state: ColumnState[] = [];
  definitions.forEach((item: ColDef) => {
    const {field, colId, sort, hide, width, flex} = item;
    if (field || colId) {
      state.push({colId: field || colId, sort, hide, width, flex});
    }
  });
  return state;
};

export interface FilterColumnsParams {
  excludedColumns: string[];
  filterableColumns: string[];
  columns: ColDef[];
};

/**
 * Filters the given columns to not include any excluded columns and remove filtering on any columns specified.
 *
 * @returns filtered columns
 */
export const filterColumns = ({
  columns,
  excludedColumns= [],
  filterableColumns = []
}: FilterColumnsParams) => (
  columns.filter(column =>
    !excludedColumns.includes(column?.field)
  ).map(column => {
    if (!filterableColumns.includes(column?.field)) {
      column.filter = false;
      column.filterParams = undefined;
    }
    return column;
  })
);
